import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';import axios from 'axios';
import { ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const NON_PEPTIDE = ['Accessories', 'Apparel', 'Skincare'];

export default function PeptidesPage() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/peptides`).then(res => setProducts(res.data)).catch(() => {});
  }, []);

  const peptides = products.filter(p => !NON_PEPTIDE.includes(p.category) && !p.id.startsWith('case-'));
  const categories = [...new Set(peptides.map(p => p.category))];

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16">
      <title>Research Peptides Canada | Canadian Peptides</title>
      <meta name="description" content="Browse Canadian Wellness & Fitness research peptides including BPC-157, TB-500, CJC-1295, Ipamorelin, Tesamorelin, Epitalon, DSIP and more." />
      <link rel="canonical" href="https://canadianwellness.net/peptides" />
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="font-['Oswald'] text-4xl sm:text-5xl lg:text-6xl uppercase tracking-wide text-white mb-6" data-testid="peptides-page-h1">
          Research Peptides in <span className="text-[#C41E3A]">Canada</span>
        </h1>

        <div className="space-y-4 text-gray-300 text-base leading-relaxed mb-12 max-w-3xl">
          <p>
            Canadian Wellness &amp; Fitness is a Canadian source for research peptides supplied strictly for
            research and educational purposes. Our catalog covers the peptides most commonly referenced in
            current scientific literature — including BPC-157, TB-500, CJC-1295, Ipamorelin, Tesamorelin,
            Epitalon, DSIP, MOTS-C, GHK-Cu, Semax, Selank and IGF-1 LR3 — organized by research category
            so you can quickly find the compound relevant to your work.
          </p>
          <p>
            Every vial is lyophilized (freeze-dried) for stability and ships Canada-wide by Express Post.
            Peptides are supplied unreconstituted; bacteriostatic water and supplies are available in our
            accessories range. Reconstituted peptides should be refrigerated, and all handling should follow
            standard laboratory practice.
          </p>
          <p>
            Each product page includes the dosage per vial, a plain-language overview of the published
            research surrounding the compound, and storage guidance. None of the statements on this site are
            medical claims — our products are not intended to diagnose, treat, cure or prevent any disease,
            and are sold for research and educational purposes only.
          </p>
        </div>

        {categories.map(cat => (
          <section key={cat} className="mb-10">
            <h2 className="font-['Oswald'] text-lg md:text-lg uppercase tracking-wider text-[#D4AF37] border-b border-[#27272A] pb-2 mb-4">
              {cat} Research Peptides
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {peptides.filter(p => p.category === cat).map(p => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group flex items-center justify-between bg-[#0A0A0A] border border-[#27272A] hover:border-[#C41E3A] rounded-sm px-4 py-3 transition-colors"
                  data-testid={`peptides-link-${p.id}`}
                >
                  <div>
                    <h3 className="text-white text-sm font-semibold uppercase tracking-wide">{p.name}</h3>
                    <p className="text-gray-500 text-xs">{p.dosage} — {p.price}{p.out_of_stock ? ' • Out of stock' : ''}</p>
                  </div>
                  <ArrowRight size={14} className="text-gray-600 group-hover:text-[#C41E3A] transition-colors flex-shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        ))}

        <section className="mb-10">
          <h2 className="font-['Oswald'] text-lg uppercase tracking-wider text-[#D4AF37] border-b border-[#27272A] pb-2 mb-4">
            Research Supplies &amp; More
          </h2>
          <p className="text-gray-400 text-sm mb-3">
            We also carry bacteriostatic water, syringes and other laboratory supplies, plus skincare and CWF apparel.
          </p>
          <Link to="/#products" className="inline-flex items-center gap-2 text-[#C41E3A] hover:text-white text-sm font-semibold uppercase tracking-wider transition-colors" data-testid="peptides-browse-all-link">
            Browse the full catalog <ArrowRight size={14} />
          </Link>
        </section>

        <p className="text-gray-600 text-xs leading-relaxed border-t border-[#27272A] pt-6">
          All products sold by Canadian Wellness &amp; Fitness are for research and educational purposes only and are
          not for human or veterinary use. Nothing on this website constitutes medical advice, and no statement has
          been evaluated by Health Canada or the FDA.
        </p>
      </div>
    </div>
  );
}
